import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import MarkdownView from '../components/MarkdownView';
import KBLayout from '../components/KBLayout';
import { kbApi, KBArticle } from '../services/kbApi';
import { useAuthStore } from '../stores/authStore';

const KBArticlePage: React.FC = () => {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [article, setArticle] = useState<KBArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!idOrSlug) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await kbApi.get(idOrSlug);
        if (alive) setArticle(res.article);
      } catch (e: any) {
        if (alive) toast.error(e?.response?.data?.message || e?.message || 'Статья не найдена');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [idOrSlug]);

  const onDelete = async () => {
    if (!article) return;
    if (!window.confirm(`Удалить статью «${article.title}»?`)) return;
    setDeleting(true);
    try {
      await kbApi.remove(article.id);
      toast.success('Статья удалена');
      navigate('/kb');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ошибка при удалении');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <KBLayout>
        <div className="text-center py-12 text-muted-foreground">Загрузка статьи…</div>
      </KBLayout>
    );
  }
  if (!article) {
    return (
      <KBLayout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-foreground">Статья не найдена</h3>
          <Link to="/kb" className="text-primary hover:underline mt-2 inline-block">К списку статей</Link>
        </div>
      </KBLayout>
    );
  }

  return (
    <KBLayout>
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/kb"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="h-4 w-4" /> К базе знаний
        </Link>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate(`/kb/${article.id}/edit`)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-card hover:bg-accent text-sm"
            >
              <PencilSquareIcon className="h-4 w-4" />
              Редактировать
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 text-sm disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" />
              {deleting ? 'Удаление…' : 'Удалить'}
            </button>
          </div>
        )}
      </div>

      <article className="bg-card border border-border rounded-lg p-5 sm:p-7">
        <header className="mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{article.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {article.status !== 'published' && (
              <span className={`px-2 py-0.5 rounded-full uppercase tracking-wide text-[10px] ${
                article.status === 'draft'
                  ? 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                  : 'bg-muted text-muted-foreground border border-border'
              }`}>
                {article.status === 'draft' ? 'черновик' : 'архив'}
              </span>
            )}
            {article.category && (
              <span className="font-medium text-foreground/80">
                {article.category}
                {(article as any).subcategory && (
                  <span className="text-muted-foreground"> › {(article as any).subcategory}</span>
                )}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <EyeIcon className="h-3.5 w-3.5" /> {article.views}
            </span>
            <span>·</span>
            <span>
              {article.author
                ? `${article.author.firstName} ${article.author.lastName}`.trim()
                : 'автор неизвестен'}
            </span>
            <span>· обновлено {new Date(article.updatedAt).toLocaleString('ru-RU')}</span>
            {article.lastEditedBy && article.author
                && article.lastEditedBy.id !== article.author.id && (
              <span>
                (ред.: {article.lastEditedBy.firstName} {article.lastEditedBy.lastName})
              </span>
            )}
          </div>
          {article.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {article.tags.map(t => (
                <span key={t} className="text-[11px] text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </header>

        <MarkdownView source={article.content} />
      </article>
    </div>
    </KBLayout>
  );
};

export default KBArticlePage;
